/**
 * ============================================================================
 * UniqueTextField — Reusable Duplicate Check Component
 * ============================================================================
 *
 * A wrapper around MaskedInput that adds real-time server-side uniqueness
 * validation triggered on blur. Designed for fields like PAN, TAN, CIN that
 * must be unique across the entire database.
 *
 * ── HOW IT WORKS ──────────────────────────────────────────────────────────────
 * 1. User types into a masked input field (masking handled by MaskedInput).
 * 2. On blur, the component sends a GET request to the duplicate-check API
 *    with the field name, value, and optional entity ID (all encrypted).
 * 3. The API responds with a boolean `true` (duplicate) or `false` (unique).
 * 4. Validation feedback is provided:
 *    - Duplicate: Shows a red border and helper text ("This value already exists").
 *    - Unique: Silently ignored (no extra message shown).
 *    - Error: Shows an orange warning text ("Unable to verify, please try again") below the field.
 *    - User Edit: If the user modifies the value after a duplicate error, the error state is instantly cleared.
 * 5. The parent is notified via onDuplicateStatusChange(true/false)
 *    so it can disable/enable the Save button.
 *
 * ── USAGE (inside a React Hook Form Controller) ──────────────────────────────
 *
 *   import UniqueTextField from 'src/components/unique-text-field/UniqueTextField';
 *
 *   // In parent component:
 *   const [duplicateFields, setDuplicateFields] = useState({ pan: false, tan: false, cin: false });
 *
 *   const handleDuplicateStatusChange = (fieldKey) => (isDuplicate) => {
 *     setDuplicateFields((prev) => ({ ...prev, [fieldKey]: isDuplicate }));
 *   };
 *
 *   const hasDuplicates = Object.values(duplicateFields).some(Boolean);
 *
 *   // In JSX (inside <Controller render={...}>):
 *   <Controller
 *     name="pan"
 *     control={control}
 *     render={({ field, fieldState }) => (
 *       <UniqueTextField
 *         field={field}
 *         error={fieldState.error}
 *         maskType="PAN"
 *         label={<div>PAN <span style={{ color: 'red', fontWeight: 'bolder' }}>*</span></div>}
 *         fieldName="clientPan"
 *         validationRegex={/^[A-Z]{5}\d{4}[A-Z]{1}$/}
 *         entityId={currentUser?.clientId || null}
 *         onDuplicateStatusChange={handleDuplicateStatusChange('pan')}
 *       />
 *     )}
 *   />
 *
 *   // On Save button:
 *   <LoadingButton disabled={hasDuplicates} ... />
 *
 * ============================================================================
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import axiosInstance from 'src/api/axiosInstance';
import { encryptData } from 'src/utils/Crypto';
import MaskedInput from 'src/utils/MaskedInput';

// ── Default API endpoint for duplicate checking ──
const DEFAULT_API_ENDPOINT = '/rest/test/check-duplicate-field';

// ── Validation status constants ──
const STATUS = {
  IDLE: 'idle',
  CHECKING: 'checking',
  DUPLICATE: 'duplicate',
  AVAILABLE: 'available',
  ERROR: 'error',
};

/**
 * A reusable masked text field component with built-in real-time server-side uniqueness validation.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.field - React Hook Form field object (from Controller's render prop).
 * @param {Object} [props.error] - React Hook Form fieldState.error — forwarded to MaskedInput.
 * @param {string} props.maskType - Mask type for MaskedInput (e.g., "PAN", "CIN", "TAN").
 * @param {React.ReactNode} [props.label] - Label for the text field.
 * @param {string} props.fieldName - API field name sent to backend for duplicate checking (e.g., "clientPan").
 * @param {RegExp} [props.validationRegex] - Format validation regex — API call is skipped if value doesn't match.
 * @param {number|null} [props.entityId=null] - Entity ID for Update operations (null for Add).
 * @param {string} [props.apiEndpoint="/rest/test/check-duplicate-field"] - Override API endpoint.
 * @param {function(boolean): void} [props.onDuplicateStatusChange] - Callback invoked when duplicate status changes.
 * @returns {React.ReactElement}
 */
export default function UniqueTextField({
  field,
  error,
  maskType,
  label,
  fieldName,
  validationRegex,
  entityId = null,
  apiEndpoint = DEFAULT_API_ENDPOINT,
  onDuplicateStatusChange,
  duplicateMessage = 'This value already exists',
  isDuplicate,
  ...other
}) {
  const isControlled = typeof isDuplicate === 'boolean';

  // ── Local state for tracking validation status ──
  const [status, setStatus] = useState(() => (isControlled && isDuplicate ? STATUS.DUPLICATE : STATUS.IDLE));

  // ── Ref to store the last checked value (avoid redundant API calls) ──
  const lastCheckedValueRef = useRef(isControlled && isDuplicate ? field.value : null);

  // ── Ref to store the status of the last checked value ──
  // Used to restore the correct status when user re-blurs without changing value.
  const lastCheckedStatusRef = useRef(isControlled && isDuplicate ? STATUS.DUPLICATE : null);

  // ── Refs for props that change reference every render ──
  // React Hook Form's Controller creates a new `field` object on each render,
  // and the parent typically creates a new callback function on each render.
  // Storing these in refs ensures handleBlur has a STABLE dependency array,
  // preventing unnecessary recreation and the subtle bugs that come with it.
  const fieldRef = useRef(field);
  fieldRef.current = field;

  const onDuplicateStatusChangeRef = useRef(onDuplicateStatusChange);
  onDuplicateStatusChangeRef.current = onDuplicateStatusChange;

  // ── Sync external isDuplicate prop changes with local state (only when explicitly provided) ──
  useEffect(() => {
    if (!isControlled) return;
    if (isDuplicate) {
      setStatus(STATUS.DUPLICATE);
      lastCheckedValueRef.current = fieldRef.current?.value;
      lastCheckedStatusRef.current = STATUS.DUPLICATE;
    } else {
      setStatus((prevStatus) => (prevStatus === STATUS.DUPLICATE ? STATUS.IDLE : prevStatus));
    }
  }, [isControlled, isDuplicate]);

  // ── Wrap field.onChange to clear duplicate message when user modifies value ──
  // When user starts editing after a duplicate was found, reset status to IDLE
  // so the error message and red border disappear immediately.
  const wrappedField = {
    ...field,
    onChange: (...args) => {
      if (status === STATUS.DUPLICATE || status === STATUS.ERROR || (isControlled && isDuplicate)) {
        setStatus(STATUS.IDLE);
        lastCheckedValueRef.current = null;
        lastCheckedStatusRef.current = null;
        onDuplicateStatusChangeRef.current?.(false);
      }
      field.onChange(...args);
    },
  };

  // ── Compute error for MaskedInput ──
  // When status is DUPLICATE or isDuplicate prop is true, pass a synthetic error so MUI renders red border.
  // RHF validation errors take priority; duplicate error is shown only when
  // there's no existing RHF error.
  const isDuplicateActive = status === STATUS.DUPLICATE || (isControlled && isDuplicate);
  const duplicateError = isDuplicateActive
    ? { message: duplicateMessage }
    : null;
  const effectiveError = error || duplicateError;

  /**
   * handleBlur — Triggered when the user leaves the field.
   * Sends a duplicate-check request to the backend with encrypted parameters.
   *
   * Guards (in order):
   * 1. Empty value → reset to idle, skip API
   * 2. Format validation failed (validationRegex) → reset to idle, skip API
   * 3. Value unchanged since last check → restore previous status, skip API
   */
  const handleBlur = useCallback(async () => {
    // Read current value from ref (always latest, no stale closure)
    const currentValue = fieldRef.current.value;

    // ── Guard 1: skip if value is empty ──
    if (!currentValue || currentValue.trim() === '') {
      setStatus(STATUS.IDLE);
      lastCheckedValueRef.current = null;
      lastCheckedStatusRef.current = null;
      onDuplicateStatusChangeRef.current?.(false);
      return;
    }

    // ── Guard 2: skip if value fails format validation ──
    // If validationRegex is provided and value doesn't match,
    // the Yup error will already be visible — no need to waste an API call.
    if (validationRegex && !validationRegex.test(currentValue)) {
      setStatus(STATUS.IDLE);
      onDuplicateStatusChangeRef.current?.(false);
      return;
    }

    // ── Guard 3: skip if value is the same as last verified value ──
    // Restores the previous status (DUPLICATE or AVAILABLE) so the
    // correct message stays visible without making another API call.
    if (currentValue === lastCheckedValueRef.current && lastCheckedStatusRef.current) {
      setStatus(lastCheckedStatusRef.current);
      onDuplicateStatusChangeRef.current?.(lastCheckedStatusRef.current === STATUS.DUPLICATE);
      return;
    }

    // ── Set checking state ──
    setStatus(STATUS.CHECKING);

    try {
      // ── Encrypt all parameters before sending ──
      const encryptedFieldName = encryptData(fieldName);
      const encryptedValue = encryptData(currentValue);
      const encryptedEntityId = encryptData(entityId); 

      // ── Make API call with encrypted params ──
      const response = await axiosInstance.get(apiEndpoint, {
        params: {
          fieldName: encryptedFieldName,
          value: encryptedValue,
          entityId: encryptedEntityId,
        },
      });

      const isDuplicateResult = response.data;

      // ── Determine the new status ──
      const newStatus = isDuplicateResult ? STATUS.DUPLICATE : STATUS.AVAILABLE;

      // ── Update status and notify parent ──
      setStatus(newStatus);
      onDuplicateStatusChangeRef.current?.(isDuplicateResult);

      // ── Store the checked value AND its result to prevent redundant calls ──
      lastCheckedValueRef.current = currentValue;
      lastCheckedStatusRef.current = newStatus;
    } catch (err) {
      // ── API call failed — show error state ──
      console.error(`[UniqueTextField] Duplicate check failed for ${fieldName}:`, err);
      setStatus(STATUS.ERROR);
      // Do not block save on network errors — let user retry
      onDuplicateStatusChangeRef.current?.(false);

      // Store value but NOT status in ref.
      // On next blur with same value, Guard 3 checks: value matches AND status exists.
      // Since status is null, Guard 3 won't trigger → API retries (desired for errors).
      // For successful checks, both value AND status are stored → Guard 3 blocks repeats.
      lastCheckedValueRef.current = currentValue;
      lastCheckedStatusRef.current = null;
    }
  // Stable deps only — field and onDuplicateStatusChange are accessed via refs
  }, [fieldName, validationRegex, entityId, apiEndpoint]);

  /**
   * getStatusMessage — Returns the inline status message and color
   * based on the current validation state.
   */
  const getStatusMessage = () => {
    switch (status) {
      case STATUS.CHECKING:
        return {
          message: 'Checking availability...',
          color: 'text.secondary',
          showSpinner: true,
        };
      case STATUS.DUPLICATE:
        return null;
      case STATUS.AVAILABLE:
        return null;
      case STATUS.ERROR:
        return {
          message: 'Unable to verify, please try again',
          color: 'warning.main',
          showSpinner: false,
        };
      default:
        return null;
    }
  };

  const statusInfo = getStatusMessage();

  return (
    <Box>
      {/* ── Masked input field with all existing masking behavior preserved ── */}
      {maskType ? (
        <MaskedInput
          field={wrappedField}
          error={effectiveError}
          maskType={maskType}
          label={label}
          onBlur={handleBlur}
        />
      ) : (
        <TextField
          {...wrappedField}
          error={!!effectiveError}
          helperText={effectiveError?.message}
          label={label}
          onBlur={(e) => {
            field.onBlur?.(e);   // preserve RHF's internal onBlur
            handleBlur(e);
          }}
          fullWidth
          {...other}
        />
      )}

      {/* ── Inline status message (shown below the input field) ── */}
      {statusInfo && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 0.5,
            ml: 1.5,
          }}
        >
          {/* Show spinner during API call */}
          {statusInfo.showSpinner && <CircularProgress size={12} />}

          <Typography
            variant="caption"
            sx={{ color: statusInfo.color }}
          >
            {statusInfo.message}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ── Prop type definitions ──
UniqueTextField.propTypes = {
  /** React Hook Form field object (from Controller's render prop) */
  field: PropTypes.object.isRequired,

  /** React Hook Form fieldState.error — forwarded to MaskedInput */
  error: PropTypes.object,

  /** Mask type for MaskedInput (e.g., "PAN", "CIN", "TAN") */
  maskType: PropTypes.string,

  /** Label for the text field */
  label: PropTypes.any,

  /** API field name sent to backend for duplicate checking (e.g., "clientPan") */
  fieldName: PropTypes.string.isRequired,

  /**
   * Format validation regex — if provided and value doesn't match,
   * the API call is skipped entirely (saves wasted calls on invalid input).
   * Example: /^[A-Z]{5}\d{4}[A-Z]{1}$/ for PAN format.
   */
  validationRegex: PropTypes.instanceOf(RegExp),

  /**
   * Entity ID for Update operations.
   * Pass null for Add (new record).
   * Pass actual ID for Update (backend will exclude this record from check).
   */
  entityId: PropTypes.number,

  /** Override API endpoint. Defaults to "/rest/ClientDetails/check-duplicate" */
  apiEndpoint: PropTypes.string,

  /**
   * Callback invoked when duplicate status changes.
   * Called with true when a duplicate is found, false when field is available.
   * Parent uses this to enable/disable the Save button.
   */
  onDuplicateStatusChange: PropTypes.func,

  /** Custom error message when a duplicate is found */
  duplicateMessage: PropTypes.string,

  /** Whether the field is currently considered duplicate by parent state */
  isDuplicate: PropTypes.bool,
};
